/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from "react";
import { tokens } from "../../theme";
import {
  Box,
  CircularProgress,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useTheme,
} from "@mui/material";
import Select from "../../components/SearchableSelect";
import Header from "../../components/Header";
import DataGridButton from "../../components/DataGridButton";
import ConfirmationDialog from "../../components/ConfirmationDialog";
import { Sync } from "@mui/icons-material";
import ReleaseSummaryView from "./SummaryView";
import ReleaseView from "./ReleaseView";
import ServiceView from "./ServiceView";
import { useGetAppGroups, useGetReleases } from "../../api/releases";
import useIamModal from "../IamModal/UseIamModal";
import { releaseStyles } from "./styles";
import { toPascalCase } from "../../utils/helperFunctions";
import { dataGridButtonTypography } from "../../styles/DataGridStyles";
import {
  dataGridButtonStyles,
  iamDividerStyles,
} from "../../styles/buttonStyles";

const views = ["summary", "release", "service"];

const getReleasesQueryParams = (view) => {
  switch (view) {
    case "service":
      return "?serviceView=true";
    case "summary":
      return "?loadRepos=false";
    default:
      return "";
  }
};

const Releases = () => {
  const theme = useTheme();
  const colors = tokens(theme.paletteId);
  const { styles } = releaseStyles(colors);

  const [apps, setApps] = useState([]);
  const [selectedApp, setSelectedApp] = useState("");
  const [isLoadingView, setIsLoadingView] = useState(true);
  const [selectedView, setSelectedView] = useState("summary");
  const [summary, setSummary] = useState(null);

  const { IamModal, IamButton, canViewIamModal } = useIamModal(
    `/apps/${selectedApp}/releases`
  );

  const {
    postData: getApps,
    response: getAppsResp,
    isLoading: isLoadingApps,
  } = useGetAppGroups();

  const {
    postData: getReleases,
    response: getReleasesResp,
    isLoading: isLoadingReleases,
  } = useGetReleases(selectedApp);

  useEffect(() => {
    getApps();
  }, []);

  useEffect(() => {
    setIsLoadingView(isLoadingApps || isLoadingReleases);
  }, [isLoadingApps, isLoadingReleases]);

  useEffect(() => {
    if (getAppsResp?.response?.length) {
      setApps(getAppsResp.response);
      setSelectedApp(
        getAppsResp.response.includes("tessell")
          ? "tessell"
          : getAppsResp.response[0]
      );
    }
  }, [getAppsResp]);

  useEffect(() => {
    if (selectedApp) {
      getReleases(getReleasesQueryParams(selectedView));
    }
  }, [selectedApp]);

  const [confirmationDialog, setConfirmationDialog] = useState({
    isOpen: false,
    content: "",
    handleDialogConfirm: () => {},
  });

  const resetDialog = () => {
    setConfirmationDialog({
      isOpen: false,
      content: "",
      handleDialogConfirm: () => {},
    });
  };

  // Summary view reports its own counts; other views only know the selected app.
  const subtitle =
    selectedView === "summary" && summary
      ? [
          `${summary.open} open release${summary.open === 1 ? "" : "s"}`,
          summary.newest?.name ? `newest ${summary.newest.name}` : "",
          `${summary.archived} archived`,
        ]
          .filter(Boolean)
          .join(" · ")
      : `Manage releases for: ${selectedApp}`;

  const getView = () => {
    if (isLoadingView) {
      return (
        <Box sx={styles.loadingBox}>
          <CircularProgress color="primary" />
        </Box>
      );
    }
    switch (selectedView) {
      case "summary":
        return (
          <ReleaseSummaryView
            selectedAppGroup={selectedApp}
            onStatsChange={setSummary}
          />
        );
      case "release":
        return <ReleaseView getReleasesResp={getReleasesResp} />;
      default:
        return <ServiceView getReleasesResp={getReleasesResp} />;
    }
  };

  return (
    <Box
      mx="5px"
      sx={{ display: "flex", flexDirection: "column", minHeight: 0 }}
    >
      <ConfirmationDialog
        isOpen={confirmationDialog.isOpen}
        handleDialogConfirm={confirmationDialog.handleDialogConfirm}
        content={confirmationDialog.content}
        resetDialog={resetDialog}
      />
      {/* Sticky header — stays pinned while the commit list scrolls */}
      <Box
        ref={(el) => {
          if (el)
            document.documentElement.style.setProperty(
              "--releases-header-height",
              `${el.offsetHeight}px`
            );
        }}
        sx={{
          position: "sticky",
          top: 0,
          zIndex: 10,
          backgroundColor: "background.default",
        }}
      >
        <Grid mt={2} container columns={2} alignItems={"center"}>
          <Grid item xs={1}>
            <Header title={selectedApp || "Releases"} subtitle={subtitle} />
          </Grid>
          <Grid item xs={1}>
            <Stack direction={"row-reverse"} spacing={1} alignItems="center">
              {IamButton}
              {/* row-reverse: the hairline renders between RELOAD and IAM. */}
              {canViewIamModal ? <Box sx={iamDividerStyles(colors)} /> : null}

              <DataGridButton
                sx={dataGridButtonStyles.pageTopButtons}
                onClick={() =>
                  getReleases(getReleasesQueryParams(selectedView))
                }
                toolTipProps={{ title: "Reload the releases list" }}
              >
                <Sync sx={{ fontSize: 16, color: "#fff" }} />
                <Typography sx={dataGridButtonTypography(colors, 500)}>
                  RELOAD
                </Typography>
              </DataGridButton>
              <ToggleButtonGroup
                exclusive
                size="small"
                value={selectedView}
                sx={styles.viewToggleGroup}
                onChange={(_, view) => {
                  if (!view) return;
                  setIsLoadingView(true);
                  setSelectedView(view);
                  getReleases(getReleasesQueryParams(view));
                }}
              >
                {views.map((view) => (
                  <ToggleButton
                    value={view}
                    key={view}
                    sx={styles.viewToggleButton}
                  >
                    {toPascalCase(view)}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
              <FormControl sx={styles.viewSelectForm}>
                <InputLabel id="select-label">App</InputLabel>
                <Select
                  labelId="select-app-label"
                  id="select-app"
                  value={selectedApp || " "}
                  sx={styles.viewSelectSelect}
                  label="App"
                  onChange={(e) => setSelectedApp(e.target.value)}
                >
                  {apps.map((app) => (
                    <MenuItem value={app} key={app}>
                      {app}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Stack>
          </Grid>
        </Grid>
      </Box>
      {IamModal}
      <Box>{getView()}</Box>
    </Box>
  );
};

export default Releases;
